/* Sheet CSS Module */

/* ============================================
   Overlay - backdrop behind the sheet
   ============================================ */
.overlay {
  background-color: var(--colors-overlay-transparent);
  inset: 0;
  opacity: 0;
  pointer-events: auto;
  position: absolute;
  touch-action: none;
  z-index: 0;
}

/* ============================================
   Styled Sheet - the main sheet panel
   ============================================ */
.styledSheet {
  background-color: var(--colors-components-sheet-background);
  border-radius: var(--vapor-sheet-radius);
  box-shadow: var(--shadows-12dp);
  display: inline-flex;
  outline: none;
  pointer-events: auto;
  position: relative;
  transition: width 0.25s var(--transitions-eases-easeInOut),
    height 0.25s var(--transitions-eases-easeInOut);
  z-index: 1;

  &[data-focus-visible] {
    box-shadow: inset 0 0 0 2px var(--colors-border-interactive-focused),
      var(--shadows-12dp);
  }
}

/* ============================================
   Offset Container - positioning wrapper
   ============================================ */
.offsetContainer {
  border-radius: var(--vapor-sheet-radius);
  display: flex;
  overflow: clip;
  pointer-events: none;
  position: var(--vapor-sheet-position);
  z-index: var(--z-indices-sheet);

  /* Placement: left */
  &[data-placement='left'] {
    bottom: var(--vapor-sheet-margin, 0);
    justify-content: flex-start;
    left: calc(var(--vapor-sheet-offset) + var(--vapor-sheet-margin));
    right: 0;
    top: var(--vapor-sheet-margin);

    & > .styledSheet {
      height: 100%;
      width: calc(100vw - var(--vapor-sheet-combined-offset));
    }

    @media screen and (min-width: 768px) {
      & > .styledSheet {
        width: var(--vapor-sheet-size);
      }
    }
  }

  /* Placement: right */
  &[data-placement='right'] {
    bottom: var(--vapor-sheet-margin);
    display: flex;
    justify-content: flex-end;
    left: 0;
    right: calc(var(--vapor-sheet-offset) + var(--vapor-sheet-margin));
    top: var(--vapor-sheet-margin);

    & > .styledSheet {
      height: 100%;
      width: calc(100vw - var(--vapor-sheet-combined-offset));
    }

    @media screen and (min-width: 768px) {
      & > .styledSheet {
        width: var(--vapor-sheet-size);
      }
    }
  }

  /* Placement: top */
  &[data-placement='top'] {
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    left: var(--vapor-sheet-margin);
    right: var(--vapor-sheet-margin);
    top: calc(var(--vapor-sheet-offset) + var(--vapor-sheet-margin));

    & > .styledSheet {
      height: calc(100vh - var(--vapor-sheet-combined-offset));
      width: 100%;
    }

    @media screen and (min-width: 768px) {
      & > .styledSheet {
        height: var(--vapor-sheet-size);
      }
    }
  }

  /* Placement: bottom */
  &[data-placement='bottom'] {
    bottom: calc(var(--vapor-sheet-offset) + var(--vapor-sheet-margin));
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    left: var(--vapor-sheet-margin);
    right: var(--vapor-sheet-margin);
    top: 0;

    & > .styledSheet {
      height: calc(100vh - var(--vapor-sheet-combined-offset));
      width: 100%;
    }

    @media screen and (min-width: 768px) {
      & > .styledSheet {
        height: var(--vapor-sheet-size);
      }
    }
  }
}

/* ============================================
   Sheet Content Container - inner wrapper
   ============================================ */
.sheetContentContainer {
  border-radius: var(--radii-8);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: clip;
  width: 100%;
}

/* ============================================
   Sheet Head Container - header with title
   ============================================ */
.sheetHeadContainer {
  align-items: center;
  border-bottom: 1px solid var(--vapor-sheet-head-border-color);
  display: flex;
  flex-shrink: 0;
  gap: var(--spaces-4);
  height: var(--spaces-48);
  justify-content: space-between;
  padding: 0 var(--spaces-16);
  user-select: none;
}
